import React from 'react';
import { Mic, ChevronDown } from 'lucide-react';
import content from '../../translations/content.json';
import vocabIcon from '../../assets/brand/VocabAI_icon_transparent.png';
import './style.css';

// QualityScoreGauge component for the visual gauge
const QualityScoreGauge = () => (
  <svg viewBox="0 0 100 40" className="w-full h-12">
    <defs>
      <linearGradient id="gaugeLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#EC4899" stopOpacity="0" />
        <stop offset="50%" stopColor="#EC4899" stopOpacity="1" />
        <stop offset="100%" stopColor="#EC4899" stopOpacity="0" />
      </linearGradient>
    </defs>
    <path d="M0 30 C 20 10, 40 35, 60 20, 80 5, 100 25" stroke="url(#gaugeLineGradient)" strokeWidth="2.5" fill="none" />
  </svg>
);

// MiniDonutChart component for the small donut chart
const MiniDonutChart = () => (
  <svg viewBox="0 0 36 36" className="w-8 h-8">
    <circle cx="18" cy="18" r="15.915" fill="none" stroke="#5E17EB" strokeWidth="4" />
    <circle cx="18" cy="18" r="15.915" fill="none" stroke="#EC4899" strokeWidth="4" strokeDasharray="60, 40" strokeDashoffset="-15" />
    <circle cx="18" cy="18" r="15.915" fill="none" stroke="#FF6B00" strokeWidth="4" strokeDasharray="25, 75" strokeDashoffset="-50" />
  </svg>
);

// AgentWaveform component for the animated waveform
const AgentWaveform = () => (
  <div className="flex items-center justify-between w-full h-8">
    {[4, 8, 12, 6, 10, 14, 8, 12, 5, 9, 13, 7, 11, 4, 8, 12, 6, 10].map((h, i) => (
      <div key={i} className="w-1 bg-white/60 rounded-full" style={{ height: `${h}px` }}></div>
    ))}
  </div>
);

const TransformBanner = () => {
  const { banner } = content;
  // Disable CTA buttons as they are not yet implemented
  const areCtaButtonsEnabled = false;

  return (
    <section className="transform-banner-section">
      <div className="transform-banner-container">
        <div className="transform-banner">
          <div className="transform-banner-content">
            {/* Left Column: Title, Subtitle, and CTA Buttons */}
            <div className="transform-banner-text-column">
              <h2 className="transform-banner-title">{banner.title}</h2>
              <p className="transform-banner-subtitle">{banner.subtitle}</p>
              <div className="transform-banner-cta-container">
                <button
                  className={`transform-banner-cta-primary ${areCtaButtonsEnabled ? 'hover:bg-purple-700' : 'opacity-50 cursor-not-allowed'}`}
                  disabled={!areCtaButtonsEnabled}
                >
                  {banner.buttons.requestDemo}
                </button>
                <button
                  className={`transform-banner-cta-secondary ${areCtaButtonsEnabled ? 'hover:bg-white/20' : 'opacity-50 cursor-not-allowed'}`}
                  disabled={!areCtaButtonsEnabled}
                >
                  {banner.buttons.bookSession}
                </button>
                <button
                  className={`transform-banner-cta-secondary ${areCtaButtonsEnabled ? 'hover:bg-white/20' : 'opacity-50 cursor-not-allowed'}`}
                  disabled={!areCtaButtonsEnabled}
                >
                  {banner.buttons.talkExpert}
                </button>
              </div>
            </div>

            {/* Right Column: Widgets */}
            <div className="transform-banner-widgets-column">
              {/* Quality Score Widget */}
              <div className="transform-banner-widget w-full sm:w-64 animate-fade-in-up animation-delay-200">
                <div className="flex justify-between items-center text-[10px] text-purple-200/80 uppercase font-semibold">
                  <span>Quality Score</span>
                  <button className="flex items-center gap-1 hover:text-white">This Month <ChevronDown className="w-3 h-3" /></button>
                </div>
                <p className="text-2xl font-black text-white my-2">92%</p>
                <QualityScoreGauge />
                <div className="flex items-end justify-between mt-2">
                  <MiniDonutChart />
                  <div className="text-[9px] font-semibold text-white/70 space-y-1.5 text-right">
                    <p>Agent Behavior: 92%</p>
                    <p>Compliance: 88%</p>
                    <p>Sentiment: 84%</p>
                  </div>
                </div>
              </div>

              {/* AI Agent Chat Widget */}
              <div className="transform-banner-widget w-full sm:w-60 animate-fade-in-up animation-delay-400">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-[#5E17EB] to-[#FF6B00] flex items-center justify-center p-1 shrink-0">
                      <img src={vocabIcon} alt="Agent Icon" className="w-full h-full object-contain" />
                    </div>
                    <p className="text-xs font-bold">Vocab AI Agent</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                    <span className="text-[9px] text-emerald-400 font-semibold">Online</span>
                  </div>
                </div>
                <p className="text-xs text-purple-100 my-3 font-medium bg-white/10 p-3 rounded-lg rounded-bl-none">Hi! How can I help you today?</p>
                <AgentWaveform />
                <div className="bg-white/10 rounded-full py-1.5 px-3 flex items-center justify-between text-xs text-white/60 mt-2">
                  <span>Send a message...</span>
                  <Mic className="w-4 h-4" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransformBanner;
